import { useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getTasks, createTask, deleteTask, updateTask, getTasksUsers,
  TaskItem, TaskStatus 
} from '../../api/tasks'
import { getProject } from '../../api/projects'
import MarkdownToolbar from './MarkdownToolbar'
import { useAuth } from '../../AuthContext'
import { apiClient } from '../../api/client'
import { 
  Plus, Search, Calendar, CheckSquare, ArrowLeft, Trash2, 
  User, ChevronDown, ChevronRight
} from 'lucide-react'

const formatDateTime = (value?: string | null) => {
  if (!value) return null
  return new Date(value).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

const isOverdue = (deadline?: string | null, status?: TaskStatus) => {
  if (!deadline || status === 'completed') return false
  const deadlineDate = new Date(deadline)
  deadlineDate.setHours(23, 59, 59, 999)
  return deadlineDate < new Date()
}

export default function TasksPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const projectIdNum = Number(projectId)
  const queryClient = useQueryClient()
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Auth check using AuthContext
  const { user: currentUser } = useAuth()

  // State
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all')
  const [userFilter, setUserFilter] = useState<number | 'all'>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newDeadline, setNewDeadline] = useState('')
  const [newAssignees, setNewAssignees] = useState<number[]>([])
  const [newParentId, setNewParentId] = useState<string>('')
  const [newChecklist, setNewChecklist] = useState<string[]>([''])
  const [collapsedTasks, setCollapsedTasks] = useState<Record<number, boolean>>({})

  // Queries
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectIdNum),
    enabled: !!projectIdNum,
  })

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks', projectIdNum],
    queryFn: () => getTasks(projectIdNum),
    enabled: !!projectIdNum,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['tasksUsers'],
    queryFn: getTasksUsers,
  })

  // Project access roles
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  // Helper check for delete permission
  const checkCanDelete = (task: TaskItem) => {
    if (!currentUser) return false
    if (currentUser.role === 'system_admin') return true
    
    // Creator can delete
    if (task.created_by && Number(task.created_by) === Number(currentUser.id)) {
      return true
    }
    
    // Project admin can delete
    const userRole = projectAccessData?.access?.find(a => a.project_id === projectIdNum)?.role_in_project
    if (userRole === 'project_admin') {
      return true
    }
    
    return false
  }

  // Mutations
  const createTaskMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectIdNum] })
      setShowAddModal(false)
      // Reset form
      setNewTitle('')
      setNewDescription('')
      setNewDeadline('')
      setNewAssignees([])
      setNewParentId('')
      setNewChecklist([''])
    },
    onError: (err) => {
      alert(`Ошибка создания задачи: ${err instanceof Error ? err.message : 'неизвестная ошибка'}`)
    }
  })

  const deleteTaskMutation = useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка удаления задачи: ${err instanceof Error ? err.message : 'неизвестная ошибка'}`)
    }
  })

  const updateTaskStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: TaskStatus }) => updateTask(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectIdNum] })
    },
    onError: (err) => {
      alert(`Ошибка обновления статуса: ${err instanceof Error ? err.message : 'неизвестная ошибка'}`)
    }
  })

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim()) {
      alert('Укажите название задачи')
      return
    }

    const checklistFiltered = newChecklist
      .map(item => item.trim())
      .filter(item => item !== '')

    createTaskMutation.mutate({
      project_id: projectIdNum,
      title: newTitle.trim(),
      description: newDescription.trim() || undefined,
      status: 'new',
      deadline: newDeadline || null,
      assignee_ids: newAssignees,
      parent_id: newParentId ? Number(newParentId) : null,
      checklist: checklistFiltered.length > 0 ? checklistFiltered : undefined
    })
  }

  const handleAddChecklistField = () => {
    if (newChecklist.length >= 20) return
    setNewChecklist([...newChecklist, ''])
  }

  const handleChecklistValueChange = (index: number, val: string) => {
    const updated = [...newChecklist]
    updated[index] = val
    setNewChecklist(updated)
  }

  const handleRemoveChecklistField = (index: number) => {
    if (newChecklist.length === 1) {
      setNewChecklist([''])
      return
    }
    const updated = newChecklist.filter((_, i) => i !== index)
    setNewChecklist(updated)
  }

  const handleDeleteTask = (id: number, title: string) => {
    if (confirm(`Удалить задачу "${title}"?`)) {
      deleteTaskMutation.mutate(id)
    }
  }

  const handleToggleStatus = (id: number, currentStatus: TaskStatus) => {
    let nextStatus: TaskStatus = 'new'
    if (currentStatus === 'new') nextStatus = 'in_progress'
    else if (currentStatus === 'in_progress') nextStatus = 'completed'
    else nextStatus = 'new'
    
    updateTaskStatusMutation.mutate({ id, status: nextStatus })
  }

  const toggleCollapse = (id: number) => {
    setCollapsedTasks(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // Filter tasks based on Search Query, Status Filter & User Filter
  const matchesFilter = (task: TaskItem) => {
    const matchSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchStatus = statusFilter === 'all' || task.status === statusFilter
    
    let matchUser = true
    if (userFilter !== 'all') {
      const selectedUserId = Number(userFilter)
      const creatorMatches = task.created_by && Number(task.created_by) === selectedUserId
      const assigneeIds = task.assignee_ids ? task.assignee_ids.split(',').map(idStr => Number(idStr.trim())) : []
      const assigneeMatches = assigneeIds.includes(selectedUserId)
      matchUser = creatorMatches || assigneeMatches
    }
    
    return matchSearch && matchStatus && matchUser
  }

  // Hierarchical list rendering helper
  // We want to construct a tree where tasks are nested under their parents.
  // In search/filter mode, if the parent doesn't match the filter, but the child does, we still display the child!
  const buildTaskTree = () => {
    const taskMap = new Map<number, TaskItem & { children: any[] }>()
    tasks.forEach(t => {
      taskMap.set(t.id, { ...t, children: [] })
    })

    const rootNodes: any[] = []
    
    taskMap.forEach(node => {
      if (node.parent_id && taskMap.has(node.parent_id)) {
        taskMap.get(node.parent_id)!.children.push(node)
      } else {
        rootNodes.push(node)
      }
    })

    return rootNodes
  }

  const renderTaskRow = (node: any, depth: number = 0): React.ReactNode => {
    const matches = matchesFilter(node)
    
    // Check if any of its children matches (to decide if we render parent even if it doesn't match)
    const hasMatchingDescendant = (n: any): boolean => {
      return n.children.some((child: any) => matchesFilter(child) || hasMatchingDescendant(child))
    }

    const shouldRenderThisNode = matches || hasMatchingDescendant(node)
    if (!shouldRenderThisNode) return null

    const hasChildren = node.children.length > 0
    const isCollapsed = collapsedTasks[node.id] || false
    const overdue = isOverdue(node.deadline, node.status)
    const checklistPercent = node.checklist_total 
      ? Math.round((node.checklist_completed / node.checklist_total) * 100) 
      : 0

    return (
      <div key={node.id} className="w-full">
        {/* Task row item */}
        <div 
          className={`flex flex-col sm:flex-row sm:items-center justify-between py-3.5 px-4 border-b border-gray-150 transition-colors hover:bg-gray-50/70 gap-3 ${
            node.status === 'completed' ? 'opacity-70 bg-gray-50/30' : ''
          } ${depth > 0 ? 'bg-white/50' : 'bg-white'}`}
          style={{ paddingLeft: `${Math.max(16, depth * 28 + 16)}px` }}
        >
          {/* Left section: Collapse icon + Checkbox + Title */}
          <div className="flex items-start sm:items-center gap-3 flex-grow min-w-0">
            {/* Collapse toggle */}
            {hasChildren ? (
              <button 
                onClick={() => toggleCollapse(node.id)}
                className="p-1 hover:bg-gray-200 rounded text-gray-500 transition shrink-0"
              >
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            ) : (
              <div className="w-6 shrink-0"></div>
            )}

            {/* Checkbox status toggle */}
            <input
              type="checkbox"
              checked={node.status === 'completed'}
              onChange={() => handleToggleStatus(node.id, node.status)}
              className="mt-1 sm:mt-0 rounded text-blue-600 focus:ring-blue-500 w-5 h-5 cursor-pointer shrink-0"
              title="Переключить статус выполнения"
            />

            {/* Title & assignee info */}
            <div className="min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <Link 
                  to={`/projects/${projectId}/tasks/${node.id}`}
                  className={`font-bold text-gray-800 hover:text-blue-600 transition text-sm sm:text-base ${
                    node.status === 'completed' ? 'line-through text-gray-400 font-medium' : ''
                  }`}
                >
                  {node.title}
                </Link>

                {/* Subtask tag */}
                {depth > 0 && (
                  <span className="text-[9px] bg-gray-100 text-gray-500 font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    Подзадача
                  </span>
                )}
                
                {/* Status Badges */}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase select-none ${
                  node.status === 'completed'
                    ? 'bg-emerald-100 text-emerald-800'
                    : node.status === 'in_progress'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {node.status === 'completed' ? 'Выполнена' : node.status === 'in_progress' ? 'В работе' : 'Новая'}
                </span>
              </div>

              {/* Assignee / Creator Details */}
              <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 flex-wrap">
                {node.creator_name && (
                  <span title="Создатель задачи">
                    От: <strong className="text-gray-500">{node.creator_name}</strong>
                  </span>
                )}
                {node.assignee_names ? (
                  <span className="flex items-center gap-0.5" title="Исполнители задачи">
                    <User className="w-3 h-3 text-gray-400" />
                    Кому: <strong className="text-gray-600 font-semibold">{node.assignee_names}</strong>
                  </span>
                ) : (
                  <span className="flex items-center gap-0.5" title="Исполнитель по умолчанию (автор)">
                    <User className="w-3 h-3 text-gray-400" />
                    Кому: <strong className="text-gray-600 font-semibold">{node.creator_name || 'Автор'}</strong>
                    <span className="text-[10px] text-gray-400 italic font-normal ml-0.5">(автор)</span>
                  </span>
                )}
                {node.status_changer_name && (
                  <span className="text-gray-500 bg-gray-50 text-gray-700 px-1.5 py-0.5 rounded border border-gray-150 text-[10px] font-semibold" title="Кто изменил статус задачи">
                    Статус изменил(а): <strong className="text-gray-600 font-semibold">{node.status_changer_name}</strong>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right section: Deadline + Checklist progress + Action buttons */}
          <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0 mt-2 sm:mt-0">
            {/* Checklist count progress */}
            {node.checklist_total > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium bg-gray-50 px-2 py-1 rounded border border-gray-100">
                <CheckSquare className="w-3.5 h-3.5 text-blue-500" />
                <span>Чек-лист: {node.checklist_completed}/{node.checklist_total}</span>
                <span className="text-[10px] bg-blue-100 text-blue-800 font-bold px-1 rounded">
                  {checklistPercent}%
                </span>
              </div>
            )}

            {/* Deadline */}
            {node.deadline && (
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border ${
                overdue
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-gray-50 text-gray-600 border-gray-100'
              }`}>
                <Calendar className="w-3.5 h-3.5" />
                {formatDateTime(node.deadline)}
              </span>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <Link
                to={`/projects/${projectId}/tasks/${node.id}`}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold px-2.5 py-1 rounded text-xs transition shrink-0"
              >
                Открыть
              </Link>
              {checkCanDelete(node) && (
                <button
                  onClick={() => handleDeleteTask(node.id, node.title)}
                  className="p-1 text-gray-400 hover:text-red-600 transition shrink-0"
                  title="Удалить задачу"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Children subtasks container */}
        {hasChildren && !isCollapsed && (
          <div className="relative">
            {/* Indent connector line */}
            <div 
              className="absolute top-0 bottom-0 bg-gray-150 w-0.5"
              style={{ left: `${Math.max(16, depth * 28 + 26)}px` }}
            ></div>
            {node.children.map((child: any) => renderTaskRow(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const rootNodes = buildTaskTree()

  return (
    <div className="space-y-6 max-w-6xl mx-auto py-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 gap-1 mb-2 font-medium">
            <ArrowLeft className="w-4 h-4" />
            Назад к проекту {project?.name}
          </Link>
          <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-2">
            ✅ Задачи проекта
          </h1>
          <p className="text-gray-500 mt-1">
            Иерархический список задач, контроль чек-листов, сроков и исполнителей
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm transition duration-150 gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Новая задача
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-4">
        <div className="flex flex-col md:flex-row items-center gap-3 w-full lg:w-auto">
          {/* Search Box */}
          <div className="relative w-full md:w-72">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Поиск задач, исполнителей, описаний..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          {/* User Filter Dropdown */}
          <div className="relative w-full md:w-60 shrink-0">
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 cursor-pointer appearance-none"
            >
              <option value="all">👥 Все пользователи</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  👤 {u.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <ChevronDown className="w-4 h-4" />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition ${
              statusFilter === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Все ({tasks.length})
          </button>
          <button
            onClick={() => setStatusFilter('new')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition ${
              statusFilter === 'new'
                ? 'bg-gray-200 text-gray-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Новые ({tasks.filter(t => t.status === 'new').length})
          </button>
          <button
            onClick={() => setStatusFilter('in_progress')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition ${
              statusFilter === 'in_progress'
                ? 'bg-amber-100 text-amber-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            В работе ({tasks.filter(t => t.status === 'in_progress').length})
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition ${
              statusFilter === 'completed'
                ? 'bg-emerald-100 text-emerald-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Выполненные ({tasks.filter(t => t.status === 'completed').length})
          </button>
        </div>
      </div>

      {/* Task Nested List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500 font-medium">Загрузка списка задач...</div>
      ) : tasks.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden divide-y divide-gray-150">
          {rootNodes.some(node => matchesFilter(node) || node.children.some((c: any) => matchesFilter(c))) ? (
            rootNodes.map(node => renderTaskRow(node, 0))
          ) : (
            <div className="text-center py-12 text-gray-400">Нет задач, соответствующих фильтру.</div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-dashed border-gray-200 rounded-xl">
          <div className="text-4xl mb-3">📋</div>
          <h3 className="text-lg font-bold text-gray-900">Задач пока нет</h3>
          <p className="text-gray-500 text-sm mt-1">Создайте свою первую задачу или подзадачу в этом проекте</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-sm shadow-sm transition"
          >
            Добавить задачу
          </button>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false)
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col my-8">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">✏️ Создать новую задачу</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[75vh]">
              {/* Parent Task Selector (to make it a subtask) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Исполнители
                  </label>
                  <div className="border border-gray-300 rounded-lg p-2.5 max-h-32 overflow-y-auto space-y-1 bg-white">
                    {users.map((user) => (
                      <label key={user.id} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={newAssignees.includes(user.id)}
                          onChange={() => {
                            setNewAssignees(prev => 
                              prev.includes(user.id) 
                                ? prev.filter(id => id !== user.id) 
                                : [...prev, user.id]
                            )
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                        <span>👤 {user.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Родительская задача (для подзадач)
                  </label>
                  <select
                    value={newParentId}
                    onChange={(e) => setNewParentId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                  >
                    <option value="">-- Нет (главная задача) --</option>
                    {tasks.map((t) => (
                      <option key={t.id} value={t.id}>
                        📁 {t.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Название задачи <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Например: Спроектировать щит автоматики"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  autoFocus
                />
              </div>

              {/* Description editor with MarkdownToolbar */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Описание задачи (Markdown)
                </label>
                <MarkdownToolbar
                  textareaRef={descriptionTextareaRef}
                  value={newDescription}
                  onChange={setNewDescription}
                  users={users}
                />
                <textarea
                  ref={descriptionTextareaRef}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Опишите задачу. Используйте панель форматирования выше для добавления стилей, списков или спойлеров..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-b-lg border-t-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Срок выполнения (дедлайн)
                  </label>
                  <input
                    type="date"
                    value={newDeadline}
                    onChange={(e) => setNewDeadline(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Checklist Section */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Чек-лист (подзадачи)
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {newChecklist.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-semibold w-5">{idx + 1}.</span>
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleChecklistValueChange(idx, e.target.value)}
                        placeholder={`Пункт чек-листа ${idx + 1}`}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveChecklistField(idx)}
                        className="text-gray-400 hover:text-red-600 transition text-sm p-1.5"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
                
                {newChecklist.length < 20 && (
                  <button
                    type="button"
                    onClick={handleAddChecklistField}
                    className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 font-semibold gap-1 mt-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Добавить пункт чек-листа
                  </button>
                )}
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-semibold transition"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createTaskMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold transition"
                >
                  {createTaskMutation.isPending ? 'Создание...' : 'Создать задачу'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
